import { useState, useMemo, useEffect } from "react";
import { ListGroup, Alert } from "react-bootstrap";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { AerialImage } from "./api/Application";
import { showInstantLocalDate, showInstantLocalTime } from "./api/Date";
import { ImageListLoaderData } from "./loaders/imageListLoader";
import { aerialImageTileSpec, thumbnailUrl } from "./api/Url";
import { ZonedDateTime } from "@js-joda/core";

// deck.gl
import DeckGL from "@deck.gl/react";
import { OrthographicView, OrthographicViewState } from "@deck.gl/core";
import { TileLayer } from "@deck.gl/geo-layers";
import { BitmapLayer } from "@deck.gl/layers";
import { useOptionalParamIds } from "./api/ApiUtil";
import { createImageProjection } from "./common/Geography";

function ExpectedImagery() {
    const baseStyle = "match-item mx-2 my-1 p-3 rounded-4 d-flex align-items-center justify-content-center border border-warning border-1 shadow";
    return <div className={baseStyle} style={{ cursor: "pointer" }}>
        Next Image Scheduled for {showInstantLocalDate(ZonedDateTime.now().plusDays(14).toInstant())}
    </div>;
}

function roundToNearest( value: number, list: number[] ): number {
    return list.reduce( ( nearest, current ) =>
        Math.abs( current - value ) < Math.abs( nearest - value ) ? current : nearest
    );
}

function getSatelliteOrAerial( gsd: number ) {
    const rounded = roundToNearest( gsd, [7.5, 15, 30, 50] );
    if( [7.5, 15].includes( rounded ) ) return "Aerial";
    return "Satellite";
}

function getGsd( gsd: number ) {
    const rounded = roundToNearest( gsd * 100, [7.5, 15, 30, 50] );
    return rounded;
}

interface AerialImageItemProps {
    image: AerialImage;
    onClick: () => void;
    isSelected: boolean;
}

function AerialImageItem( { image, onClick, isSelected }: AerialImageItemProps ) {
    const selStyle   = "border border-primary";
    const unselStyle = "border border-light";
    const baseStyle  = "match-item mx-2 my-1 mb-1 p-3 rounded-4 d-flex align-items-center border-1 shadow";
    const style      = `${isSelected ? selStyle: unselStyle} ${baseStyle}`;
    const gsd        = roundToNearest(image.pixelWidth * 100, [7.5, 15, 30, 50]);
    const height     = image.height;
    const pxHeight   = Math.abs(image.pixelHeight);
    const width      = image.width;
    const pxWidth    = Math.abs(image.pixelWidth);
    const area       = ((height * pxHeight) * (width * pxWidth));
    const kmArea     = (area / 1000000).toFixed(0);
    const src        = getSatelliteOrAerial(gsd);

    return <div className={style}
        style={{ cursor: "pointer" }}
        onClick={onClick}>
        <div className="me-3 d-flex flex-column flex-shrink-0">
            <img
                src={thumbnailUrl(image.aerialImageId, 128)}
                alt={`${image.name} thumbnail`}
                className="match-item-thumbnail border rounded"
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover"
                }}
            />
        </div>
        <div className="d-flex flex-column gap-1 px-1 py-3">
            <div className="small text-muted">
                {src}
            </div>
            <div
                className="small"
                style={{
                    display: "grid",
                    gridTemplateColumns: "max-content max-content max-content",
                    gap: "0.25em"
                }}>
                <div>{showInstantLocalDate(image.capturedWhen)}</div>
                <div style={{ justifySelf: "end" }}>{showInstantLocalTime(image.capturedWhen)}</div>
                <div></div>

                <div>{getGsd( image.pixelWidth )} cm GSD</div>
                {/* <div></div> */}
                <div style={{ justifySelf: "end" }}>{kmArea} km<sup>2</sup></div>
                <div></div>

                <div>EPSG</div>
                <div style={{ justifySelf: "end" }}>{image.epsgCode}</div>
                <div></div>

                <div>Size</div>
                <div>{image.width} x {image.height}</div>
                <div></div>

            </div>
        </div>
    </div>;
}

export function ImageListPage() {
    //url params and search params
    const nav = useNavigate();
    const { aerialImageId } = useOptionalParamIds();    
    const [query, setQuery] = useSearchParams();
    const [lat, lng] = [Number( query.get( "lat" ) ), Number( query.get( "lng" ) )]    
    const hasLatLng = lat && lng;
    
    //loader and selected image handling
    const { images, error: loaderError } = useLoaderData() as ImageListLoaderData;    
    const selectedImage = useMemo( () => {
        return aerialImageId ? images.find( x => x.aerialImageId === aerialImageId ) : undefined;
    }, [aerialImageId, images] );    

    //lat and lng support
    const projection = useMemo( () => {
        if( !selectedImage ) { return undefined; }
        return createImageProjection( selectedImage );
    }, [selectedImage] );

    const initViewState: OrthographicViewState = useMemo( () => {
        const img = selectedImage; //alias for brevity
        if( img && projection && hasLatLng ) {            
            const [x, y] = projection.latLngToPixel( lat!, lng! )!;
            console.log( `x${x} y${y}`);
            return ( { target: [x, y, 0], zoom: 0 } );    
        }
        const x   = img ? Math.round( img.width  / 2 ) : 0;
        const y   = img ? Math.round( img.height / 2 ) : 0;
        console.log( `x${x} y${y}`);
        return ( { target: [x, y, 0], zoom: 0 } );
    }, [selectedImage, projection, lat, lng] );

    const handleImageClick = ( image: AerialImage ) => {
        if( image.aerialImageId === selectedImage?.aerialImageId ) { return; }
        nav( `/image/${image.aerialImageId}` );
    };

    const deckTileLayer = useMemo(() => {
        if( !selectedImage ) { return null; }
        const width  = selectedImage.width;
        const height = selectedImage.height;
        return new TileLayer<ImageBitmap>({
            id: "aerial-tiles",
            pickable: false,
            tileSize: 512,
            refinementStrategy: "best-available",
            minZoom: -3,
            maxZoom: 0,
            extent: [0, 0, width, height],
            data: aerialImageTileSpec(selectedImage.aerialImageId),
            renderSubLayers: props => {
                const [[left, bottom], [right, top]] = props.tile.boundingBox;
                const { data, ...otherProps } = props;
                return new BitmapLayer(otherProps, {
                    image: data,
                    bounds: [left, top, right, bottom],
                    textureParameters: {
                        minFilter: "nearest",
                        magFilter: "nearest"
                    }
                });
            }
        });
    }, [selectedImage] );

    console.log( "render" );
    return <div id="capture-list-page">
        <div style={{ display: "grid", gridTemplateRows: "1fr", overflow: "hidden" }}>
            {loaderError ? (
                <Alert variant="danger" className="m-3">
                    {loaderError}
                </Alert>
            ) : images.length === 0 ? (
                <Alert variant="info" className="m-3">
                    No satellite images found.
                </Alert>
            ) : (
                <ListGroup variant="flush y-scroll-auto">
                    {/* <ExpectedImagery /> */}
                    {images.map((image) => (
                        <AerialImageItem
                            key={image.aerialImageId}
                            image={image}
                            onClick={() => handleImageClick(image)}
                            isSelected={selectedImage?.aerialImageId === image.aerialImageId}
                        />
                    ))}
                </ListGroup>
            )}
        </div>
        <div style={{ backgroundColor: "black" }} className="image-viewer h-100 overflow-hidden position-relative" >
            {selectedImage && deckTileLayer && (
                <DeckGL
                    views={new OrthographicView()}
                    layers={[deckTileLayer]}
                    initialViewState={initViewState}
                    controller
                    style={{ backgroundColor: "black" }}
                    getCursor={({ isDragging }) => isDragging ? "grabbing" : "grab"}
                />
            )}
        </div>
    </div>;
}